// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS D'OFFRE — grille editeur + lecture/ecriture du jsonb `offres.services`
// ─────────────────────────────────────────────────────────────────────────────
// PARTAGE (Dom, 24/08/2026) :
//   - la GRILLE est fournie par l'EDITEUR — vocabulaire metier standard du
//     gardiennage ouest-africain. Meme patron que `cataloguePrestations.js`.
//   - la COMPOSITION appartient au TENANT.
//
// ⚠️ PARAMETRES LIBREMENT SAISIS (Dom, 25/08/2026) — CHANGEMENT DE MODELE.
// Une liste fermee 1h/2h/4h FORCE un tenant qui ne peut tenir que 6h a cocher 4h
// faute de mieux : il s'engage alors sur une promesse INTENABLE, et le futur
// suivi SLA le met en defaut. Les capacites reelles varient — trafic d'Abidjan,
// zone rurale, taille de la reserve d'agents.
//   -> nombre + unite, SAISIS LIBREMENT.
//   -> le systeme SUGGERE des valeurs courantes, il ne les IMPOSE PAS.
//
// ⚠️ MAIS PAS DE TEXTE LIBRE : « sous 2h », « 2 heures » et « 120 min » ne
// s'agregent pas. La valeur reste STRUCTUREE — { nombre, unite } — donc
// MESURABLE. C'est la condition du suivi SLA et du portail client.
// `enMinutes()` et `parAn()` le prouvent : deux ecritures differentes de la meme
// duree donnent le meme nombre.
//
// ⚠️ MIGRATION VERS `offre_composants` (lot 1) : chaque element porte
// { code, inclus, parametre } — soit une future ligne de table, `parametre`
// eclatable en (parametre_nombre, parametre_unite).

export const VERSION_SERVICES = 3;

// ── TYPES DE PARAMETRE ─────────────────────────────────────────────────────
export const UNITES = {
  delai:     ['minutes', 'heures', 'jours'],
  frequence: ['par_semaine', 'par_mois', 'par_trimestre'],
  quantite:  [],   // nombre simple, sans unite
};

const RENDU_UNITE = {
  minutes:       (n) => `${n} min`,
  heures:        (n) => `${n}h`,
  jours:         (n) => `${n} jour${n > 1 ? 's' : ''}`,
  par_semaine:   (n) => `${n} fois par semaine`,
  par_mois:      (n) => `${n} fois par mois`,
  par_trimestre: (n) => `${n} fois par trimestre`,
};

export const LIBELLE_UNITE = {
  minutes: 'minutes', heures: 'heures', jours: 'jours',
  par_semaine: 'par semaine', par_mois: 'par mois', par_trimestre: 'par trimestre',
};

// ── GRILLE EDITEUR ─────────────────────────────────────────────────────────
// `suggestions` = ERGONOMIE, jamais contrainte. Rien n'empeche 6h, 90 min ou
// 3 fois par trimestre : le tenant s'engage sur ce qu'il peut REELLEMENT tenir.
const D = (n, unite) => ({ nombre: n, unite });

export const COMPOSANTS = [
  { code: 'agent_qualifie', libelle: 'Agent qualifié et formé' },
  // ⚠️ SCISSION (Dom, 25/08/2026) — l'ancien composant « Remplacement » confondait
  // DEUX engagements de nature differente, et le libelle etait AMBIGU pour le client :
  //   « Remplacement sous 7 jours » pouvait se lire « en cas d'absence, mon site
  //     reste sans garde une semaine » ;
  //   « sous 2h » lu comme un remplacement DEFINITIF est un engagement intenable.
  // Les UNITES sont restreintes par composant : c'est ce qui empeche la confusion
  // de revenir. Une continuite de service ne peut PAS s'exprimer en jours, un
  // remplacement definitif ne peut PAS s'exprimer en heures.
  { code: 'continuite_service', libelle: 'Continuité de service', parametre: {
      type: 'delai', libelle: 'Délai', uniteDefaut: 'heures',
      unites: ['minutes', 'heures'],
      suggestions: [D(30, 'minutes'), D(1, 'heures'), D(2, 'heures'), D(4, 'heures')],
      rendu: (t) => `Agent absent remplacé sous ${rendreValeur(t)}` } },
  { code: 'remplacement_definitif', libelle: 'Remplacement définitif', parametre: {
      type: 'delai', libelle: 'Délai', uniteDefaut: 'jours',
      unites: ['jours'],
      suggestions: [D(3, 'jours'), D(7, 'jours'), D(15, 'jours')],
      rendu: (t) => `Remplacement définitif sous ${rendreValeur(t)}` } },
  { code: 'supervision_tel', libelle: 'Supervision téléphonique quotidienne' },
  { code: 'rapport_mensuel', libelle: 'Rapport mensuel' },
  { code: 'portail_client', libelle: 'Portail client' },
  { code: 'supervision_physique', libelle: 'Supervision physique', parametre: {
      type: 'frequence', libelle: 'Fréquence', uniteDefaut: 'par_mois',
      suggestions: [D(1, 'par_semaine'), D(2, 'par_mois'), D(1, 'par_mois')],
      rendu: (t) => `Supervision physique ${rendreValeur(t)}` } },
  { code: 'visite_inopinee', libelle: 'Visite inopinée direction', parametre: {
      type: 'frequence', libelle: 'Fréquence', uniteDefaut: 'par_trimestre',
      suggestions: [D(1, 'par_mois'), D(1, 'par_trimestre')],
      rendu: (t) => `Visite inopinée direction — ${rendreValeur(t)}` } },
  { code: 'rondes_police', libelle: 'Rondes forces de l\'ordre', parametre: {
      type: 'frequence', libelle: 'Fréquence', uniteDefaut: 'par_mois',
      suggestions: [D(2, 'par_mois'), D(4, 'par_mois'), D(1, 'par_semaine')],
      rendu: (t) => `Rondes forces de l'ordre — ${rendreValeur(t)}` } },
  { code: 'sla_intervention', libelle: 'SLA intervention', parametre: {
      type: 'delai', libelle: 'Délai', uniteDefaut: 'minutes',
      suggestions: [D(15, 'minutes'), D(30, 'minutes'), D(1, 'heures')],
      rendu: (t) => `SLA intervention < ${rendreValeur(t)}` } },
  { code: 'rapport_digital', libelle: 'Rapport digital temps réel' },
  { code: 'telesurveillance', libelle: 'Télésurveillance complémentaire' },
  { code: 'gps_tracking', libelle: 'GPS tracking agents' },
  { code: 'audit_annuel', libelle: 'Audit sécurité annuel offert' },
  { code: 'score_ia', libelle: 'Score de sécurité mensuel IA' },
];

export const definition = (code) => COMPOSANTS.find(c => c.code === code) || null;

export function rendreValeur(t) {
  if (!estValeurValide(t)) return '';
  const f = RENDU_UNITE[t.unite];
  return f ? f(t.nombre) : String(t.nombre);
}

/** Unites autorisees pour un composant : restriction propre, sinon celles du type. */
export function unitesAutorisees(def) {
  if (!def?.parametre) return [];
  return def.parametre.unites ?? UNITES[def.parametre.type] ?? [];
}

export function estValeurValide(t) {
  if (!t || typeof t !== 'object') return false;
  const n = Number(t.nombre);
  if (!Number.isFinite(n) || n <= 0) return false;
  return t.unite === null || t.unite === undefined || Object.keys(RENDU_UNITE).includes(t.unite);
}

// ── MESURABILITE ───────────────────────────────────────────────────────────
// Ce qui prouve que la structure suffit au suivi SLA : deux ecritures
// differentes de la meme duree donnent le MEME nombre.
export function enMinutes(t) {
  if (!estValeurValide(t)) return null;
  const n = Number(t.nombre);
  if (t.unite === 'minutes') return n;
  if (t.unite === 'heures') return n * 60;
  if (t.unite === 'jours') return n * 1440;
  return null;                       // ce n'est pas une duree
}

export function parAn(t) {
  if (!estValeurValide(t)) return null;
  const n = Number(t.nombre);
  if (t.unite === 'par_semaine') return n * 52;
  if (t.unite === 'par_mois') return n * 12;
  if (t.unite === 'par_trimestre') return n * 4;
  return null;                       // ce n'est pas une frequence
}

// ── LECTURE — TROIS formats coexistent en production ───────────────────────
//  (1) PLAT       : seed Fox — { agent_qualifie: true, remplacement_delai: "4h" }
//  (2) v2 SCALAIRE: { version: 2, composants: [{ code, inclus, parametre: "24h" }] }
//                   ⚠️ REELLEMENT PRESENT EN PROD (offre ESS de GLADIATOR).
//  (3) v3 STRUCTURE: parametre = { nombre, unite }
// Ne jamais restreindre la lecture au seul v3 : on casserait des offres servies.

// Reprise des scalaires historiques : "4h", "30min", "2j".
function parseScalaire(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'object') return estValeurValide(v) ? v : null;
  const s = String(v).trim().toLowerCase();
  let m = s.match(/^(\d+(?:[.,]\d+)?)\s*min(?:utes?)?$/);
  if (m) return { nombre: Number(m[1].replace(',', '.')), unite: 'minutes' };
  m = s.match(/^(\d+(?:[.,]\d+)?)\s*h(?:eures?)?$/);
  if (m) return { nombre: Number(m[1].replace(',', '.')), unite: 'heures' };
  m = s.match(/^(\d+(?:[.,]\d+)?)\s*j(?:ours?)?$/);
  if (m) return { nombre: Number(m[1].replace(',', '.')), unite: 'jours' };
  return null;                       // non interpretable -> pas de valeur inventee
}

// L'ancien format n'avait pas de parametre pour `supervision_physique`, mais son
// libelle etait fige a « /2 semaines ». On reprend l'equivalent STRUCTURE pour
// que la carte de Fox n'affiche RIEN de moins qu'avant.
const REPRISE = { supervision_physique: { nombre: 2, unite: 'par_mois' } };

// ── REPRISE DE L'ANCIEN COMPOSANT « remplacement » (scindé le 25/08/2026) ──
// Routage DETERMINISTE PAR L'UNITE — on ne devine JAMAIS l'intention :
//   jours            -> remplacement_definitif  (engagement contractuel)
//   minutes / heures -> continuite_service      (absence non programmee)
// C'est la seule information objective portee par la donnee existante.
// ⚠️ Une valeur en heures HORS de la plage realiste de la continuite
// (30 min - 4h) est routee ici quand meme, puis SIGNALEE par
// `valeurHorsPlage()` : on migre sans perte, on alerte, on ne corrige pas
// a la place du tenant.
const CODE_HISTORIQUE = 'remplacement';
function codeApresScission(valeur) {
  if (!estValeurValide(valeur)) return 'continuite_service';   // sans unite : le cas le plus frequent
  return valeur.unite === 'jours' ? 'remplacement_definitif' : 'continuite_service';
}

/** Plages realistes, INDICATIVES — pour signaler, jamais pour refuser. */
const PLAGES = {
  continuite_service:     { min: 30,   max: 240 },      // 30 min a 4h, en minutes
  remplacement_definitif: { min: 4320, max: 21600 },    // 3 a 15 jours, en minutes
};

export function valeurHorsPlage(code, valeur) {
  const p = PLAGES[code];
  if (!p || !estValeurValide(valeur)) return null;
  const m = enMinutes(valeur);
  if (m === null || (m >= p.min && m <= p.max)) return null;
  return code === 'continuite_service'
    ? 'Hors de la plage habituelle pour une absence non programmée (30 min à 4h).'
    : 'Hors de la plage habituelle pour un remplacement définitif (3 à 15 jours).';
}

export function normaliser(services) {
  const src = services || {};

  if (Array.isArray(src.composants)) {
    const parCode = new Map();
    for (const c of src.composants) {
      // Ancien code : on le range sous le composant issu de la scission.
      const cle = c.code === CODE_HISTORIQUE ? codeApresScission(parseScalaire(c.parametre)) : c.code;
      parCode.set(cle, c);
    }
    return COMPOSANTS.map(def => {
      const c = parCode.get(def.code);
      return {
        code: def.code,
        inclus: !!c?.inclus,
        parametre: c ? parseScalaire(c.parametre) : null,
      };
    });
  }

  return COMPOSANTS.map(def => {
    if (def.code === 'continuite_service' || def.code === 'remplacement_definitif') {
      const v = parseScalaire(src.remplacement_delai);
      const cible = codeApresScission(v);
      return { code: def.code, inclus: !!v && cible === def.code, parametre: cible === def.code ? v : null };
    }
    if (def.code === 'sla_intervention') {
      const v = parseScalaire(src.sla_intervention);
      return { code: def.code, inclus: !!v, parametre: v };
    }
    const inclus = src[def.code] === true;
    return { code: def.code, inclus, parametre: inclus ? (REPRISE[def.code] ?? null) : null };
  });
}

// ── ECRITURE ───────────────────────────────────────────────────────────────
export function versJsonb(composants) {
  return {
    version: VERSION_SERVICES,
    composants: composants.map(c => ({
      code: c.code,
      inclus: !!c.inclus,
      parametre: c.inclus && estValeurValide(c.parametre)
        ? { nombre: Number(c.parametre.nombre), unite: c.parametre.unite ?? null }
        : null,                      // un exclu ne garde pas de parametre
    })),
  };
}

// ── VALIDATION ─────────────────────────────────────────────────────────────
// REGLE : un composant INCLUS sans son PARAMETRE = offre INCOMPLETE.
// C'est ce qui rend le « Remplacement sous » vide structurellement impossible.
export function valider(composants) {
  const manquants = [];
  for (const c of composants) {
    if (!c.inclus) continue;
    const def = definition(c.code);
    if (!def?.parametre) continue;
    if (!estValeurValide(c.parametre)) { manquants.push(def); continue; }
    // L'unite doit appartenir au TYPE du composant : un delai n'est pas une
    // frequence. C'est ce qui garantit l'agregation.
    const attendues = unitesAutorisees(def);
    if (attendues.length && !attendues.includes(c.parametre.unite)) manquants.push(def);
  }
  return {
    ok: manquants.length === 0,
    manquants,
    message: manquants.length === 0 ? null
      : `Composant${manquants.length > 1 ? 's' : ''} inclus sans engagement : ${manquants.map(d => d.libelle).join(', ')}`,
  };
}

// ── AFFICHAGE ──────────────────────────────────────────────────────────────
// Le libelle est COMPOSE : « Remplacement sous 6h », jamais « Remplacement sous ».
// Sans parametre, on rend le libelle NU — degrade, jamais tronque.
export function libelleAffiche(code, parametre) {
  const def = definition(code);
  if (!def) return '';
  if (!def.parametre) return def.libelle;
  return estValeurValide(parametre) ? def.parametre.rendu(parametre) : def.libelle;
}

/** Lignes pretes a afficher sur la carte d'offre, dans l'ordre de la grille. */
export function lignesAffichage(services) {
  return normaliser(services).map(c => ({
    code: c.code,
    inclus: c.inclus,
    libelle: libelleAffiche(c.code, c.parametre),
  }));
}
